// RewardUnlockedPage — After claiming a reward
// Layout (matches attached photo):
//   - Top nav: < Back | Reward | Share/Download icons
//   - Big stock icon centered
//   - "{Stock} Inc." heading
//   - "Free stock unlocked!" subtitle
//   - Details card (Stock, Unlock date, Reward value, Shares, Price, Reward type)
//   - "Invite friends, earn more" card

import { useLocation, useNavigate } from 'react-router-dom';
import { STOCKS, REWARD_OFFER } from '../../data/stocks';

export function RewardUnlockedPage() {
  const nav = useNavigate();
  const loc = useLocation();
  const stock = (loc.state as any)?.stock || STOCKS[0];

  return (
    <div className="min-h-screen bg-white pb-8">
      {/* Top nav */}
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border bg-white/85 px-2 backdrop-blur-xl">
        <button onClick={() => nav('/portal/dashboard')} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Reward</div>
        <div className="flex items-center gap-1">
          <button className="flex h-9 w-9 items-center justify-center text-tf-blue" aria-label="Download">
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 4v12M6 12l6 6 6-6M4 20h16" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </button>
          <button className="flex h-9 w-9 items-center justify-center text-tf-blue" aria-label="Share">
            <svg viewBox="0 0 0 24 24" className="h-5 w-5" fill="currentColor"><path d="M18 16.1c-.8 0-1.4.3-2 .8L8.9 13a3 3 0 000-2l7.1-3.9c.5.5 1.2.8 2 .8 1.7 0 3-1.3 3-3s-1.3-3-3-3-3 1.3-3 3c0 .2 0 .4.1.6L7.9 9.4c-.5-.5-1.2-.8-2-.8-1.7 0-3 1.3-3 3s1.3 3 3 3c.8 0 1.5-.3 2-.8L15 13.6c-.1.2-.1.4-.1.6 0 1.7 1.3 3 3 3s3.1-1.4 3.1-3.1-1.3-3-3-3z" /></svg>
          </button>
        </div>
      </div>

      <div className="px-6 pt-12">
        <div className="flex justify-center">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-white border border-tf-border" style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.10)' }}>
            <img src={stock.icon} alt={stock.name} className="h-14 w-14 rounded-full" />
          </div>
        </div>
        <h1 className="mt-6 text-center text-[26px] font-bold tracking-tight text-tf-text-1">{stock.name} Inc.</h1>
        <p className="mt-1 text-center text-[15px] text-tf-text-3">Free stock unlocked!</p>

        <div className="mt-8 overflow-hidden rounded-[16px] border border-tf-border bg-white">
          <DetailRow label="Stock" value={`${stock.name} Inc. (${stock.symbol})`} />
          <DetailRow label="Unlock date" value={new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} />
          <DetailRow label="Reward value" value={`$${REWARD_OFFER.stockValue.toFixed(2)}`} />
          <DetailRow label="Shares received" value={`≈ ${(REWARD_OFFER.stockValue / stock.price).toFixed(3)} ${stock.symbol}`} />
          <DetailRow label="Price at reward" value={`$${stock.price.toFixed(2)}`} />
          <DetailRow label="Reward type" value="Promotional reward" last />
        </div>

        <div className="mt-4 overflow-hidden rounded-[16px] border border-tf-border bg-white">
          <button onClick={() => nav('/portal/affiliate')} className="flex w-full items-center gap-3 px-4 py-3 active:bg-[#E5E5EA]/60">
            <svg viewBox="0 0 24 24" className="h-5 w-5 text-tf-text-1" fill="none" stroke="currentColor" strokeWidth="1.8">
              <circle cx="9" cy="8" r="3" /><circle cx="17" cy="9" r="2.5" /><path d="M3 20c0-3 3-5 6-5s6 2 6 5M14 14c2 0 5 1 5 4" strokeLinecap="round" />
            </svg>
            <div className="flex-1 text-left">
              <div className="text-[15px] font-semibold text-tf-text-1">Invite friends, earn more</div>
              <div className="text-[12px] text-tf-text-3">Get free stock rewards when your friends start investing</div>
            </div>
            <svg viewBox="0 0 24 24" className="h-4 w-4 text-tf-text-muted" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </button>
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, value, last }: { label: string; value: string; last?: boolean }) {
  return (
    <div className={['flex items-center justify-between px-4 py-3', last ? '' : 'border-b border-tf-border'].join(' ')}>
      <span className="text-[14px] text-tf-text-3">{label}</span>
      <span className="text-[14px] font-medium text-tf-text-1">{value}</span>
    </div>
  );
}
